<template>
	<glass-card class="add-meal-container mt-3 mb-5">
		<div class="row">
			<h1 class="col-auto">Add</h1>
			<div class="col">
				<button class="btn btn-primary btn-lg" @click="handleBack">
					<i class="bi bi-arrow-left"></i> BACK
				</button>
			</div>
		</div>

		<form @submit.prevent="submitMeal" class="add-meal-form">
			<div class="mb-5">
				<tags
					:meal="fakeMeal"
					transparent="true"
					@cookType="(v) => (cookType = v)"
					@mealType="(v) => (mealType = v)"
					@restaurantName="(v) => (restaurantName = v)"
					@customRestaurant="handleCustomRestaurant" />
			</div>

			<div class="mb-5">
				<label for="Prompt" class="form-label"
					>Describe the item or give additional info (optional)</label
				>
				<textarea
					id="Prompt"
					class="form-control"
					v-model="prompt"
					rows="3"></textarea>
			</div>

			<image-upload v-model="imagePreview" />

			<!-- Image Preview -->
			<div v-if="imagePreview" class="image-preview mb-3">
				<img
					:src="imagePreview"
					alt="Meal preview"
					class="img-thumbnail mt-2" />
			</div>

			<!-- Date and Time Picker -->
			<div class="mb-3">
				<label for="mealDateTime" class="form-label">Date and Time</label>
				<input
					type="datetime-local"
					class="form-control"
					id="mealDateTime"
					v-model="mealDateTime" />
			</div>

			<!-- Submit Button -->
			<div class="d-flex justify-content-center my-4">
				<button
					type="submit"
					class="btn btn-success btn-lg px-4 py-3 fs-3 submit-meal-btn">
					Submit Meal
				</button>
			</div>
		</form>
	</glass-card>
</template>

<script setup>
	import { ref, onBeforeUnmount } from "vue"
	import Swal from "sweetalert2"
	import { useRouter, useRoute } from "vue-router"
	import Tags from "@/components/Tags.vue"
	import { useItemQueueStore } from "@/stores/item-queue"
	import { showAd, upgradeAd } from "@/utils/adBus"
	import ImageUpload from "@/components/ImageUpload.vue"
	import { bloodSugarNotif } from "@/utils/notif"
	import checkNewRestaurant from "@/utils/newRestaurant"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"
	import { Capacitor } from "@capacitor/core"
	// import { Filesystem, Directory, Encoding } from "@capacitor/filesystem"
	import { useUserStore } from "@/stores/user"
	import GlassCard from "@/components/GlassCard.vue"

	// Pinia store and router
	const iqStore = useItemQueueStore()
	const router = useRouter()
	const route = useRoute()
	const userStore = useUserStore()

	// Meal Data
	// const selectedCategory = ref(null)
	const currentDate = new Date()
	const timezoneOffset = currentDate.getTimezoneOffset() * 60000
	const localISOTime = new Date(currentDate - timezoneOffset)
		.toISOString()
		.slice(0, 16)
	const mealDateTime = ref(localISOTime)
	const cookType = ref("")
	const mealType = ref("")
	const prompt = ref(null)
	const restaurantName = ref("")
	const fakeMeal = ref({
		cook_type: "home-cooked",
		meal_type: "default",
		restaurant_name: "",
	})

	// Custom restaurant data
	const customRestaurantData = ref(null)

	// Image data
	const imagePreview = ref(null)

	// async function saveState() {
	// 	//save in file
	// 	await Filesystem.writeFile({
	// 		path: "state.json",
	// 		data: JSON.stringify({
	// 			route: "/add",
	// 			selectedCategory: selectedCategory.value,
	// 			cookType: cookType.value,
	// 			mealType: mealType.value,
	// 			prompt: prompt.value,
	// 			restaurantName: restaurantName.value,
	// 			mealDateTime: mealDateTime.value,
	// 		}),
	// 		directory: Directory.Data,
	// 		encoding: Encoding.UTF8,
	// 	})
	// }

	// function selectCategory(category) {
	// 	selectedCategory.value = category
	// 	if (!category) {
	// 		// Clear memory
	// 		if (imagePreview.value) {
	// 			URL.revokeObjectURL(imagePreview.value)
	// 			imagePreview.value = null
	// 		}
	// 		cookType.value = ""
	// 		mealType.value = ""
	// 		prompt.value = ""
	// 		restaurantName.value = ""
	// 		mealDateTime.value = localISOTime
	// 		fakeMeal.value.cook_type = "home-cooked"
	// 		fakeMeal.value.meal_type = "default"
	// 		fakeMeal.value.restaurant_name = ""
	// 		customRestaurantData.value = null
	// 	}
	// }

	// Handle custom restaurant data from Tags component
	function handleCustomRestaurant(data) {
		customRestaurantData.value = data
	}

	/**
	 * Submits the meal to the local queue and schedules a notification if enabled by the user.
	 */
	const submitMeal = async () => {
		// Validate image for 'food' category
		// if (selectedCategory.value === "food" && !imagePreview.value) {
		// 	Swal.fire({ icon: "error", title: "Please select an image first." })
		// 	return
		// }

		//must have an image or description otherwise give an error
		if (!imagePreview.value && !prompt.value) {
			Swal.fire({
				icon: "error",
				title: "Please provide an image or a description.",
			})
			return
		}

		await checkNewRestaurant(cookType.value, customRestaurantData.value)

		// Build the offline item object
		const tempId = `tempId-${new Date().valueOf()}`
		const newItem = {
			id: tempId,
			status: "sending",
			// category: selectedCategory.value,
			mealDateTime: mealDateTime.value,
			cookType: cookType.value,
			mealType: mealType.value,
			prompt: prompt.value,
			restaurantName: restaurantName.value,
			imageSrc: imagePreview.value,
		}

		// Add to queue in localStorage
		await iqStore.load()
		await iqStore.add(newItem)

		// Handle notifications based on user settings

		if (!SubscriptionHelper.isFreeAccount()) {
			if (userStore.user?.bloodSugarNotifications && mealType.value) {
				await bloodSugarNotif(
					mealType.value,
					userStore.user?.bloodSugarNotificationDelay,
					mealDateTime.value
				)
			}
		}

		// Clean up UI and memory
		// if (imagePreview.value) {
		// 	URL.revokeObjectURL(imagePreview.value)
		// }
		imagePreview.value = null

		// Show ad and redirect
		if (SubscriptionHelper.isFreeAccount()) {
			if (Capacitor.isNativePlatform()) {
				showAd.value = true
			} else {
				upgradeAd.value = true
			}
		}

		router.push({ name: "dashboard" })
	}

	function handleBack() {
		// if (!selectedCategory.value) {
		router.push({ name: "dashboard" })
		// } else {
		// 	selectCategory("")
		// }
	}

	// onBeforeMount(async () => {
	// 	const params = new URLSearchParams(window.location.search)
	// 	const type = params.get("type")
	// 	if (type === "food" || type === "drink") {
	// 		selectCategory(type)
	// 	}

	// 	// const restored = params.get("restored")
	// 	// alert("RESTORED:" + restored)
	// 	// if (restored === "from_camera") {
	// 	// 	//load the file
	// 	// 	const state = await Filesystem.readFile({
	// 	// 		path: "state.json",
	// 	// 		directory: Directory.Data,
	// 	// 		Encoding: Encoding.UTF8,
	// 	// 	})
	// 	// 	const stateObj = JSON.parse(state.data)
	// 	// 	alert(state.data)

	// 	// 	//put in all the values
	// 	// 	selectedCategory.value = stateObj.selectedCategory
	// 	// 	cookType.value = stateObj.cookType
	// 	// 	mealType.value = stateObj.mealType
	// 	// 	prompt.value = stateObj.prompt
	// 	// 	restaurantName.value = stateObj.restaurantName
	// 	// 	mealDateTime.value = stateObj.mealDateTime
	// 	// 	// imagePreview.value = stateObj.cameraData

	// 	// 	//empty the state file
	// 	// 	await Filesystem.deleteFile({
	// 	// 		path: "state.json",
	// 	// 		directory: Directory.Data,
	// 	// 	})
	// 	// }
	// })

	onBeforeUnmount(() => {
		if (imagePreview.value) {
			// URL.revokeObjectURL(imagePreview.value)
			imagePreview.value = null
		}
	})
</script>

<style lang="less" scoped>
	// @import "../assets/colors.less";

	.selection-buttons {
		display: flex;
		justify-content: center;
		gap: 20px;
		margin-top: 20px;
		.btn-lg {
			padding: 10px 30px;
			font-size: 36px;
			border-radius: 10px;
			i {
				margin-right: 10px;
			}
		}
	}

	.add-meal-container {
		max-width: 600px;
		margin: auto;
		padding: 1rem;
	}

	.image-preview img {
		max-width: 100%;
		height: auto;
	}

	/* Transparent inputs for this view */
	.form-control {
		background-color: transparent !important;
		color: #ffffff !important;
		border-color: rgba(255, 255, 255, 0.25);
	}
	.form-control:focus {
		background-color: transparent !important;
		color: #ffffff !important;
		box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
		border-color: #22c55e;
	}
	.form-floating > label {
		background-color: transparent !important;
		color: #cbd5e1;
	}
	.form-control::placeholder {
		color: #94a3b8;
		opacity: 0.8;
	}
</style>
